import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Temporal } from '@js-temporal/polyfill';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { EmployeeReviewDialogComponent, UserBulkInsertComponent } from 'src/app/dialogs';
import { ReviewTypeEnum } from 'src/app/enums/review-type.enum';
import { ReviewPeriodHelper } from 'src/app/helpers/review-period.helper';
import { UserBulkInsertDialogResult } from 'src/app/models/dialog/user-bulk-insert-dialog-result.model';
import { EmployeeForAssessments } from 'src/app/models/employee-for-assessments.model';
import { Employee } from 'src/app/models/employee.model';
import { EmployeeBulkInsertDialogConfig } from 'src/app/models/emplyee-bulk-insert-dialog-config.model';
import { IResultModel, ResultModel } from 'src/app/models/general/result.model';
import { OpenAssessmentEmployee } from 'src/app/models/open-assessment-employee.model';
import { PagingRequest } from 'src/app/models/paging-request.model';
import { ReviewPeriodEmployee } from 'src/app/models/review-period-employee.model';
import { Search } from 'src/app/models/search.model';
import { State } from 'src/app/models/state.model';
import { AssessmentService, EmployeeService, StateService } from 'src/app/services';
import { DateHelper } from 'src/app/shared/helpers/date.helper';
import { ListItem } from 'src/app/shared/models/list-item.model';
import { ErrorMessageService } from 'src/app/shared/services';
import { AppState } from 'src/app/store/app.state';
import { SettingsState } from 'src/app/store/settings.state';
import { TranslateService } from '@ngx-translate/core';
import { EmployeeHelper } from 'src/app/helpers/employee.helper';

@UntilDestroy()
@Component({
  selector: 'app-missing-reviews',
  templateUrl: './missing-reviews.component.html',
  styleUrls: ['./missing-reviews.component.scss']
})
export class MissingReviewsComponent implements OnInit {

  private readonly itemsSubject = new BehaviorSubject<ListItem<OpenAssessmentEmployee>[]>([]);
  public readonly items$ = this.itemsSubject.asObservable();

  public searchControl: UntypedFormControl;

  public minCharCount = 1;

  public viewMode?: string = 'byEmployee';
  public EmployeeHelper = EmployeeHelper;

  // paging
  public total = 0;
  public pageSize = 10;
  public pageSizeOptions = [5, 10, 20, 50];
  public page = 0;
  public state: State;
  
  public searchMessage = '';
  
  public get isValidCharacterCount(): boolean {
    return this.searchControl.value.length === 0 || this.searchControl.value.length >= this.minCharCount;
  }

  @ViewChild('paginator')
  paginator: MatPaginator;
    
  public get searchValue(): string {
    return this.searchControl.value;
  }

  public get hasSubitems(): boolean {
    return this.settingState.reviewPeriod?.value.reviewType == ReviewTypeEnum.TER;
  }

  public setPageSizeOptions(setPageSizeOptionsInput: string) {
    if (setPageSizeOptionsInput) {
      this.pageSizeOptions = setPageSizeOptionsInput.split(',').map(str => +str);
    }
  }

  public get isPanoramaOrAura(): boolean {
    return this.settingState.reviewPeriod?.value?.reviewType == ReviewTypeEnum.TER;
  }

  public get isEditable(): boolean {
    return ReviewPeriodHelper.isBlockEditable(this.settingState, this.state);
  }

  constructor(
    public settingState: SettingsState,
    public appState: AppState,
    public stateService: StateService,
    private assessmentService: AssessmentService,
    private employeeService: EmployeeService,
    private msgService: ErrorMessageService,
    private dialog: MatDialog,
    private translate: TranslateService
    ) {
      this.state = stateService.state;
  }

  ngOnInit(): void {    
    this.searchControl = new UntypedFormControl('');
    
    combineLatest([this.settingState.reviewPeriod.obs$, this.settingState.reloadReviewPeriodEmployeeList.obs$, this.settingState.missingReviews.asObservable()])
      .pipe(untilDestroyed(this))
      .subscribe(([reviewPeriod, reload]) => {
        if (reviewPeriod || reload)
         this.loadList();
    });

    this.searchControl.valueChanges.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.firstPage();
          this.setValidationMessage();
        }),
        switchMap(searchString => searchString.length === 0 || searchString.length >= this.minCharCount ? this.employeeSearch(searchString): of([])),
        untilDestroyed(this)
    ).subscribe(itemArray => {
      this.itemsSubject.next(itemArray);    
    });
    
  }

  public pagingSettingsChanged(data: PageEvent)
  {
    this.page = data.pageIndex;
    this.pageSize = data.pageSize;
    this.loadList();
  }

  private employeeSearch(searchString: string): Observable<ListItem<OpenAssessmentEmployee>[]> {
    if (!this.settingState.reviewPeriod.value) return of([]);
    
    return this.assessmentService.getOpen(this.buildPageRequest(searchString)).pipe(
      tap(data => {
        this.total = data.total;
        this.searchMessage = this.getResultMessage(data.list.length);
      }),
      map(result => result.list.map(x => new ListItem(x)) )
    );    
  }

  private buildPageRequest(searchString: string): Search {
    let data = {
      search: searchString,
      reviewPeriodId: this.settingState.reviewPeriod.value.id,
      viewMode: this.viewMode,
      pagingRequestInfo: {
        pageSize: this.pageSize,
        page: this.page + 1
      } as PagingRequest
    } as Search;

    return data;
  }

  public clearText() {
    this.searchControl.setValue('');
    //this.loadList();
  }

  public isDateExpired(date: Temporal.PlainDate): boolean {
    if (!date) return false;
    
    return DateHelper.isDateExpired(date);
 }

  private loadList() {
    this.employeeSearch(this.searchControl.value)
    .pipe(untilDestroyed(this))
    .subscribe(itemArray => {
      this.itemsSubject.next(itemArray);      
    });
  }
   
  public setValidationMessage() {
    if (!this.isValidCharacterCount)
      this.searchMessage = this.translate.instant('MISSING_REVIEWS.MIN_CHAR_MESSAGE', { minCharCount: this.minCharCount });
  }

  public getResultMessage(count: number): string {
      if (this.total > count)
        return this.translate.instant('MISSING_REVIEWS.RESULTS_MESSAGE', { total: this.total, count: count });
      else if (count === 0)
        return this.translate.instant('MISSING_REVIEWS.NO_RESULTS');
      else
        return '';
  }

  public rowEdit(openAssessmentItem: OpenAssessmentEmployee, employee: EmployeeForAssessments) {
    
    let rpe: ReviewPeriodEmployee;

    if (this.viewMode == 'byEmployee')
    {
      rpe = {
        reviewPeriodEmployeeId: employee.reviewPeriodEmployeeId ?? openAssessmentItem.mainItem.reviewPeriodEmployeeId,
        byEmployee: openAssessmentItem.mainItem as Employee,
        aboutEmployee: employee as Employee,
        reviewPeriod: this.settingState.reviewPeriod.value
      } as ReviewPeriodEmployee;
    }
    else
    {
      rpe = {
        reviewPeriodEmployeeId: employee.reviewPeriodEmployeeId ?? openAssessmentItem.mainItem.reviewPeriodEmployeeId,
        byEmployee: employee as Employee,
        aboutEmployee: openAssessmentItem.mainItem as Employee,
        reviewPeriod: this.settingState.reviewPeriod.value
      } as ReviewPeriodEmployee;
    }

    const dialogRef = this.dialog.open(EmployeeReviewDialogComponent, {
      minWidth: "500px",
      data: rpe,
    });

    dialogRef.afterClosed().subscribe( (dialogResult: boolean) => {
      if (dialogResult)
      {         
        this.settingState.reloadReviewPeriodEmployeeList.value = true;
      }
    });

  }

  public add() {

    const data = {
      reviewPeriod: this.settingState.reviewPeriod.value,
    } as ReviewPeriodEmployee;

    const dialogRef = this.dialog.open(EmployeeReviewDialogComponent, {
      minWidth: "500px",      
      data: data
    });

    dialogRef.afterClosed().subscribe( (dialogResult: boolean) => {
      if (dialogResult)
      {         
        this.settingState.reloadReviewPeriodEmployeeList.value = true;
      }
   });
  }

  public massAddPeers() {

    const rpId = this.settingState.reviewPeriod.value.id;

    //popup
    const dialogRef = this.dialog.open(UserBulkInsertComponent, {
      width: '770px',
      data: {
        id: rpId,
        title: this.translate.instant('MISSING_REVIEWS.ADD_PEERS_TITLE'),
        reviewPeriod: this.settingState.reviewPeriod.value,
        validateAction: this.employeeService.checkEmails
       } as EmployeeBulkInsertDialogConfig
    });

    dialogRef.afterClosed().subscribe( (result: UserBulkInsertDialogResult) => {
      if (result) {
        this.assessmentService.addPeers(result.items, rpId, result.aboutEmployeeId)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (data) => {
            this.msgService.showSnackBarMessage('successfulAction');
            this.loadList();
          }, 
          error: (err: IResultModel<ResultModel>) => {
            this.msgService.handleError<ResultModel>(err);
          }
        });
      }
    });
  }

  public rowDelete(rpeId: number) {
    this.assessmentService.deleteReviewPeriodEmployee(rpeId)
    .pipe(untilDestroyed(this))
    .subscribe({
      next: () => {
        this.settingState.reloadReviewPeriodEmployeeList.value = true;
        this.msgService.showSnackBarMessage('successfulAction');
      },
      error: (err: IResultModel<ResultModel>) => {
        this.msgService.handleError<ResultModel>(err);
      }
    });    
  }

  public viewByEmployee() {
    this.viewMode = 'byEmployee';
    this.loadList();
  }

  public viewAboutEmployee() {
    this.viewMode = 'aboutEmployee';
    this.loadList();
  }

  public getEmployeeType(item: EmployeeForAssessments): string {
    if (item.isSelf) return this.translate.instant('MISSING_REVIEWS.SELF_ASSESSMENT');
    if (item.isLeader) return this.translate.instant('MISSING_REVIEWS.LEADER');
    if (item.isSubordinate) return this.translate.instant('MISSING_REVIEWS.SUBORDINATE');
    if (!item.isSelf && !item.isLeader && !item.isSubordinate) return this.translate.instant('MISSING_REVIEWS.PEER');
    return '';
  }
  
}
